/**
 * Brand-categorised spare parts catalog.
 *
 * Each item is mapped to a brand category slug declared in `productCategories.js`
 * (kind: 'brand'). Keep this file as the single source of truth for SKU rows —
 * the homepage grid, category landing pages, individual product detail pages,
 * and the sitemap all read from here.
 *
 * Item shape (all SEO-relevant fields are intentionally first-class):
 *   {
 *     partNumber:       string  // The OEM/aftermarket SKU printed in column B.
 *     name:             string  // English product name (column D).
 *     slug:             string  // URL slug — derived from name + part number.
 *     cnName:           string? // Original Chinese description (column C), kept for cross-language search.
 *     systemCategory:   string? // Slug of a system-category in productCategories.js (for cross-linking).
 *     description:      string  // 1–3 sentence sales/SEO description, unique per item.
 *     applicationModels:string[]// Compatible machine models — boosts long-tail search.
 *     keywords:         string  // Comma-separated keyword list for <meta name="keywords">.
 *   }
 *
 * The starter set below contains the rows I could reliably read from the
 * supplied screenshot. Once the source Excel/CSV is shared, additional rows
 * should be appended to the matching brand array — no other file changes needed.
 */

export const BRAND_ITEMS = {
  'liugong-wheel-loader': [
    {
      partNumber: '11C0021',
      name: 'Water Pump',
      slug: 'water-pump-11c0021',
      cnName: '水泵',
      systemCategory: 'cooling-radiator',
      description:
        'Heavy-duty engine water pump for LiuGong wheel loaders. Cast iron housing, high-flow impeller, and double-lip mechanical seal keep coolant moving under sustained load-and-carry duty cycles.',
      applicationModels: ['CLG835', 'CLG842', 'CLG855N', 'CLG856H'],
      keywords:
        'LiuGong water pump 11C0021, CLG855N water pump, wheel loader cooling pump, LiuGong engine spare parts',
    },
    {
      partNumber: '11C1611',
      name: 'Oil Pump',
      slug: 'oil-pump-11c1611',
      cnName: '机油泵',
      systemCategory: 'engine-parts',
      description:
        'Engine oil pump assembly for LiuGong wheel loaders. Precision-machined rotor and pressure-relief valve maintain galleries pressurised through stop-start quarry and yard work.',
      applicationModels: ['CLG835', 'CLG855N'],
      keywords:
        'LiuGong oil pump 11C1611, CLG855N engine oil pump, wheel loader lubrication pump',
    },
    {
      partNumber: '11C1612',
      name: 'Oil Pump',
      slug: 'oil-pump-11c1612',
      cnName: '机油泵',
      systemCategory: 'engine-parts',
      description:
        'Alternate-spec engine oil pump for LiuGong wheel loaders. Direct-fit OEM replacement engineered for sustained pressure on continuous-duty loading shifts.',
      applicationModels: ['CLG842', 'CLG856H'],
      keywords:
        'LiuGong oil pump 11C1612, CLG856H lubrication pump, heavy equipment oil pump replacement',
    },
    {
      partNumber: '11C2621',
      name: 'Gear Pump',
      slug: 'gear-pump-11c2621',
      cnName: '齿轮泵',
      systemCategory: 'hydraulic-components',
      description:
        'External gear pump for LiuGong wheel loader auxiliary hydraulics. Hardened gears and load-tolerant bushings deliver stable flow to steering and brake-charging circuits.',
      applicationModels: ['CLG855N', 'CLG856H', 'CLG862H'],
      keywords:
        'LiuGong gear pump 11C2621, CLG856H hydraulic gear pump, wheel loader pilot pump',
    },
    {
      partNumber: '11C1614',
      name: 'HV Pump',
      slug: 'hv-pump-11c1614',
      cnName: '液压泵',
      systemCategory: 'hydraulic-components',
      description:
        'High-volume hydraulic pump for LiuGong wheel loader work circuits. Optimised for bucket lift and tilt cycle times under heavy material loads.',
      applicationModels: ['CLG855N', 'CLG856H'],
      keywords:
        'LiuGong HV pump 11C1614, CLG855N hydraulic pump, wheel loader main pump',
    },
    {
      partNumber: '11C2611',
      name: 'HV Pump',
      slug: 'hv-pump-11c2611',
      cnName: '液压泵',
      systemCategory: 'hydraulic-components',
      description:
        'Direct-replacement high-volume hydraulic pump for LiuGong loaders. Restores breakout force and cycle speed after pump-related performance loss.',
      applicationModels: ['CLG856H', 'CLG862H'],
      keywords:
        'LiuGong HV pump 11C2611, CLG862H hydraulic pump, loader work pump replacement',
    },
    {
      partNumber: '11C1641',
      name: 'HV Pump',
      slug: 'hv-pump-11c1641',
      cnName: '液压泵',
      systemCategory: 'hydraulic-components',
      description:
        'Heavy-flow hydraulic pump for LiuGong wheel loader lift and tilt hydraulics. Tight tolerances and improved seal package extend service life on abrasive sites.',
      applicationModels: ['CLG855N', 'CLG856H'],
      keywords:
        'LiuGong HV pump 11C1641, CLG855N main hydraulic pump, wheel loader spare parts',
    },
    {
      partNumber: '11C2631',
      name: 'Steering Pump',
      slug: 'steering-pump-11c2631',
      cnName: '转向泵',
      systemCategory: 'brakes-steering',
      description:
        'Power-steering pump for LiuGong wheel loaders. Maintains pilot pressure for orbitrol-driven steering and load-sensing priority valves on mid-size wheel loaders.',
      applicationModels: ['CLG855N', 'CLG856H'],
      keywords:
        'LiuGong steering pump 11C2631, CLG856H steering pump, wheel loader power steering',
    },
    {
      partNumber: '11C0181',
      name: 'Steering Pump',
      slug: 'steering-pump-11c0181',
      cnName: '转向泵',
      systemCategory: 'brakes-steering',
      description:
        'Steering hydraulic pump for LiuGong wheel loaders. Replacement-grade unit engineered for predictable feel and consistent emergency-steering reserve.',
      applicationModels: ['CLG835', 'CLG842'],
      keywords:
        'LiuGong steering pump 11C0181, CLG842 steering pump, wheel loader hydraulic steering',
    },
    {
      partNumber: '41A0002',
      name: 'Planetary Gear',
      slug: 'planetary-gear-41a0002',
      cnName: '行星齿轮',
      systemCategory: 'transmission-drivetrain',
      description:
        'High-precision planetary gear designed for optimal torque transmission and durability in heavy-duty wheel loader drivetrains. Heat-treated alloy steel construction with OEM-spec geometry for noise-free engagement.',
      applicationModels: ['CLG855N', 'CLG856H', 'CLG862H'],
      keywords:
        'LiuGong planetary gear 41A0002, CLG856H planetary gear, wheel loader transmission gear, OEM replacement',
    },
    {
      partNumber: '4110000146',
      name: 'Sun Gear',
      slug: 'sun-gear-4110000146',
      cnName: '太阳轮',
      systemCategory: 'transmission-drivetrain',
      description:
        'Central sun gear for the planetary stage on LiuGong wheel loader transmissions. Case-carburised teeth resist pitting under shock-loaded forward/reverse changes.',
      applicationModels: ['CLG855N', 'CLG856H'],
      keywords:
        'LiuGong sun gear 4110000146, CLG855N planetary sun gear, wheel loader gearbox parts',
    },
  ],

  'sdlg-wheel-loader': [
    {
      partNumber: '4120001739',
      name: 'Working Pump',
      slug: 'working-pump-4120001739',
      cnName: '工作泵',
      systemCategory: 'hydraulic-components',
      description:
        'Main working hydraulic pump for SDLG wheel loaders. Restores lift and tilt performance on production yard work with a wear-resistant gear-pump cartridge.',
      applicationModels: ['LG936L', 'LG946L', 'LG953', 'LG956L'],
      keywords:
        'SDLG working pump 4120001739, LG956L main pump, SDLG wheel loader hydraulic pump',
    },
    {
      partNumber: '4120000076',
      name: 'Steering Pump',
      slug: 'steering-pump-4120000076',
      cnName: '转向泵',
      systemCategory: 'brakes-steering',
      description:
        'Steering hydraulic pump for SDLG wheel loaders. Drop-in fit for OEM steering circuits, calibrated for predictable orbitrol response under load.',
      applicationModels: ['LG936L', 'LG953', 'LG956L'],
      keywords:
        'SDLG steering pump 4120000076, LG956L steering pump, SDLG wheel loader parts',
    },
    {
      partNumber: '4644306334',
      name: 'Brake Disc',
      slug: 'brake-disc-4644306334',
      cnName: '制动盘',
      systemCategory: 'brakes-steering',
      description:
        'Wet brake disc for SDLG wheel loader service brakes. Friction surface optimised for sustained heat dissipation on graded haul roads.',
      applicationModels: ['LG953', 'LG956L', 'LG958L'],
      keywords:
        'SDLG brake disc 4644306334, LG956L brake disc, wheel loader wet brake parts',
    },
    {
      partNumber: '29040007331',
      name: 'Hydraulic Cylinder',
      slug: 'hydraulic-cylinder-29040007331',
      cnName: '液压油缸',
      systemCategory: 'hydraulic-components',
      description:
        'Bucket tilt hydraulic cylinder for SDLG wheel loaders. Chrome-plated rod and reinforced seal stack designed for abrasive quarry environments.',
      applicationModels: ['LG953', 'LG956L'],
      keywords:
        'SDLG hydraulic cylinder 29040007331, LG956L tilt cylinder, wheel loader cylinder',
    },
    {
      partNumber: '4120000098',
      name: 'Control Valve',
      slug: 'control-valve-4120000098',
      cnName: '控制阀',
      systemCategory: 'hydraulic-components',
      description:
        'Main control valve assembly for SDLG wheel loaders. Multi-spool layout supports lift, tilt, and auxiliary functions with load-holding check valves.',
      applicationModels: ['LG953', 'LG956L'],
      keywords:
        'SDLG control valve 4120000098, LG956L main control valve, wheel loader hydraulic valve',
    },
    {
      partNumber: '4110000054',
      name: 'Air Conditioner Compressor',
      slug: 'air-conditioner-compressor-4110000054',
      cnName: '空调压缩机',
      systemCategory: 'cab-operator-station',
      description:
        'Cabin A/C compressor for SDLG wheel loaders. Restores cooling capacity in hot quarry and yard environments without modifying the cab harness.',
      applicationModels: ['LG953', 'LG956L', 'LG958L'],
      keywords:
        'SDLG A/C compressor 4110000054, LG956L air conditioner compressor, wheel loader cab parts',
    },
  ],

  'xcmg-wheel-loader': [
    {
      partNumber: '860111111',
      name: 'Charging Pump',
      slug: 'charging-pump-860111111',
      cnName: '充液泵',
      systemCategory: 'transmission-drivetrain',
      description:
        'Transmission charging pump for XCMG wheel loaders. Maintains converter and clutch-pack supply pressure for crisp shift quality on heavy load cycles.',
      applicationModels: ['LW300FN', 'LW500FN', 'ZL50GN'],
      keywords:
        'XCMG charging pump 860111111, LW500FN charging pump, XCMG wheel loader transmission parts',
    },
    {
      partNumber: '803004041',
      name: 'Steering Pump',
      slug: 'steering-pump-803004041',
      cnName: '转向泵',
      systemCategory: 'brakes-steering',
      description:
        'Hydraulic steering pump for XCMG wheel loaders. Calibrated for emergency-steer reserve and consistent feel on grade work.',
      applicationModels: ['LW500FN', 'ZL50GN'],
      keywords:
        'XCMG steering pump 803004041, LW500FN steering pump, XCMG wheel loader parts',
    },
    {
      partNumber: '252800018',
      name: 'Working Pump',
      slug: 'working-pump-252800018',
      cnName: '工作泵',
      systemCategory: 'hydraulic-components',
      description:
        'Primary working hydraulic pump for XCMG wheel loaders. Drop-in OEM replacement that restores lift and tilt cycle times on production sites.',
      applicationModels: ['LW500FN', 'ZL50GN'],
      keywords:
        'XCMG working pump 252800018, ZL50GN hydraulic pump, XCMG wheel loader main pump',
    },
    {
      partNumber: '275101559',
      name: 'Control Valve',
      slug: 'control-valve-275101559',
      cnName: '多路阀',
      systemCategory: 'hydraulic-components',
      description:
        'Multi-way control valve for XCMG wheel loader work hydraulics. Includes load-sense porting for predictable simultaneous lift-and-tilt operation.',
      applicationModels: ['LW500FN', 'ZL50GN'],
      keywords:
        'XCMG control valve 275101559, LW500FN multi way valve, XCMG wheel loader hydraulic valve',
    },
  ],

  'zf-transmission-parts': [
    {
      partNumber: '4644306334',
      name: 'Clutch Disc',
      slug: 'clutch-disc-4644306334',
      cnName: '离合器片',
      systemCategory: 'transmission-drivetrain',
      description:
        'Friction clutch disc for ZF powershift transmissions used in wheel loaders and articulated dump trucks. Sintered friction surface for stable engagement on shock loads.',
      applicationModels: ['ZF 4WG200', 'ZF 4WG180'],
      keywords:
        'ZF clutch disc 4644306334, ZF 4WG200 friction plate, powershift transmission clutch disc',
    },
    {
      partNumber: '4644354034',
      name: 'Clutch Disc',
      slug: 'clutch-disc-4644354034',
      cnName: '离合器片',
      systemCategory: 'transmission-drivetrain',
      description:
        'High-energy ZF clutch friction disc for medium-duty powershift transmissions. Engineered for low drag, high friction stability, and repeatable shift quality.',
      applicationModels: ['ZF 4WG200', 'ZF 6WG200'],
      keywords:
        'ZF clutch disc 4644354034, ZF 6WG200 friction plate, ZF transmission clutch',
    },
    {
      partNumber: '4644354103',
      name: 'Clutch Disc',
      slug: 'clutch-disc-4644354103',
      cnName: '离合器片',
      systemCategory: 'transmission-drivetrain',
      description:
        'ZF powershift clutch disc — long-life friction lining for reverse and forward clutch packs in earth-moving transmissions.',
      applicationModels: ['ZF 4WG200', 'ZF 6WG200'],
      keywords:
        'ZF clutch disc 4644354103, ZF 4WG200 reverse clutch, ZF transmission spare parts',
    },
    {
      partNumber: '4644308029',
      name: 'Clutch Disc',
      slug: 'clutch-disc-4644308029',
      cnName: '离合器片',
      systemCategory: 'transmission-drivetrain',
      description:
        'OEM-spec ZF clutch friction disc for wheel loader and grader transmissions. Direct replacement profile, oil-compatible with ZF TE-ML 03 fluids.',
      applicationModels: ['ZF 4WG180', 'ZF 4WG200'],
      keywords:
        'ZF clutch disc 4644308029, ZF 4WG180 clutch plate, ZF transmission service parts',
    },
    {
      partNumber: '4644351096',
      name: 'Clutch Disc',
      slug: 'clutch-disc-4644351096',
      cnName: '离合器片',
      systemCategory: 'transmission-drivetrain',
      description:
        'ZF clutch disc with paper-friction lining for low-shock engagement. Ideal rebuild component for high-hour wheel loader transmissions.',
      applicationModels: ['ZF 4WG200'],
      keywords:
        'ZF clutch disc 4644351096, ZF 4WG200 clutch friction plate, ZF rebuild kit',
    },
    {
      partNumber: 'SP102928',
      name: 'Seal Kit',
      slug: 'seal-kit-sp102928',
      cnName: '密封件套件',
      systemCategory: 'transmission-drivetrain',
      description:
        'Comprehensive shaft and housing seal kit for ZF powershift transmissions. Includes lip seals, O-rings, and gaskets matched to OEM fluid-resistance specs.',
      applicationModels: ['ZF 4WG200'],
      keywords:
        'ZF seal kit SP102928, ZF 4WG200 seal kit, transmission overhaul seals',
    },
    {
      partNumber: 'SP102934',
      name: 'Lift Kit',
      slug: 'lift-kit-sp102934',
      cnName: '提升套件',
      systemCategory: 'transmission-drivetrain',
      description:
        'Service-piston lift kit for ZF clutch packs. Restores apply pressure response on rebuilt powershift transmissions.',
      applicationModels: ['ZF 4WG200'],
      keywords:
        'ZF lift kit SP102934, ZF 4WG200 piston kit, transmission clutch service parts',
    },
  ],
};

// Helpers ---------------------------------------------------------------------

/**
 * Get the full items array for a given brand-category slug.
 * Returns an empty array if the brand has no seeded items yet.
 */
export function getItemsForBrand(brandSlug) {
  return BRAND_ITEMS[brandSlug] ?? [];
}

/**
 * Lookup a single item by brand slug + item slug.
 * Returns `null` when the pair does not match (callers should render a 404).
 */
export function getItemBySlugs(brandSlug, itemSlug) {
  const list = BRAND_ITEMS[brandSlug];
  if (!list) return null;
  return list.find((item) => item.slug === itemSlug) ?? null;
}

/**
 * Build the canonical detail-page path for a brand item.
 * Centralised so we never hand-build these URLs in components.
 */
export function itemPath(brandSlug, itemSlug) {
  return `/category/${brandSlug}/${itemSlug}`;
}

/**
 * Flatten all items across brands into [{ brandSlug, item }] tuples.
 * Used by the sitemap generator and any "all products" listings.
 */
export function getAllBrandItems() {
  return Object.entries(BRAND_ITEMS).flatMap(([brandSlug, items]) =>
    items.map((item) => ({ brandSlug, item })),
  );
}
